/* Printed card text → structured effects.
 *
 * The POC's insight was right and is kept: parse the printed text so that every
 * card is playable, including brews, instead of only a curated scripted pool.
 * What's fixed here is the precision.
 *
 *  - [DON!! xN] reads N off the card instead of assuming 2.
 *  - A cost clause ("You may trash 1 card from your hand:") is separated from
 *    the effect it pays for, so the two no longer both fire unconditionally.
 *  - "your opponent draws" no longer hands YOU the card.
 *  - "up to" / "you may" mark an effect optional rather than mandatory.
 *
 * Anything unrecognised is returned as an `unparsed` clause. The engine turns
 * those into an explicit manual prompt rather than silently dropping them.
 */

/* Bandai prints curly apostrophes, en/em dashes and a real ×. */
const OPP = String.raw`opponent(?:['’]s)?`;
const DASH = String.raw`[-−–—]`;

export const normalize = s => String(s ?? '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/’/g, "'")
  .replace(/×/g, 'x')
  .replace(/\r/g, '')
  .trim();

export const cardText = c => normalize(c?.effect);
export const triggerText = c => normalize(c?.trigger);

const num = s => {
  const m = String(s).replace(/,/g, '').match(/-?\d+/);
  return m ? +m[0] : 0;
};

/* ---------------------------------------------------------------------- */
/* clause boundaries                                                       */
/* ---------------------------------------------------------------------- */

/* Bandai separates a card's clauses with <br>, and `normalize` turns those into
 * newlines. Splitting on newlines was therefore the whole clause splitter.
 *
 * The dump the app actually loads strips them. Not one of the 2,348 cards with
 * effect text arrives carrying a newline, so every card collapsed to a SINGLE
 * clause: the first timing tag claimed the whole card, and every later clause's
 * ops fired under it, behind the first clause's cost.
 *
 * Concretely, Ohm — "[On Play] DON!! -1: Draw 1 card.<br>[When Attacking] …give
 * -1000 power" — became one On Play clause that drew AND debuffed, and had no
 * [When Attacking] ability at all. Across the database that is 249 cards losing
 * every clause after the first, including 48 [Activate: Main] abilities that no
 * longer existed to be activated and 62 [Counter] clauses.
 *
 * So split on the tags themselves, which are present whatever the source does
 * to the markup. Newlines still split — a source that keeps <br> is more
 * explicit than this inference, not less.
 */

/* Bracket tags that begin a clause: the timings, plus the conditions that only
 * ever appear as a prefix to one. Deliberately NOT keywords ([Blocker],
 * [Rush]) or card names ([Enel]), which appear mid-sentence and must not
 * split — "your opponent cannot activate [Blocker] during this battle" is one
 * clause, not two. */
const CLAUSE_TAG = new RegExp(
  '^(?:On Play|Main|When Attacking|On K\\.?O\\.?|On Block|Activate: Main|Counter'
  + '|Trigger|End of (?:Your )?Turn|On Your Opponent\'s Attack|Your Turn'
  + '|Opponent\'s Turn|Once Per Turn|Once Per Game|DON!!\\s*x\\s*\\d+)$', 'i');

/* The tags that make a clause an EVENT — something that resolves at a moment —
 * as opposed to a condition tag on an always-on clause. */
const EVENT_TAG = new RegExp(
  '\\[(?:On Play|Main|When Attacking|On K\\.?O\\.?|On Block|Activate: Main'
  + '|Counter|Trigger|End of (?:Your )?Turn|On Your Opponent\'s Attack)\\]', 'i');

/**
 * A card's printed text as separate clauses.
 *
 * Splits on newlines and on runs of leading bracket tags, so the same card
 * reads identically whether its source preserved <br> or flattened it away.
 * Text before the first tagged clause is kept as its own segment — that is
 * where a card's always-on ability lives, and folding it into the next clause
 * is how it used to end up inside an [On Play] cost string.
 */
export function splitClauses(text) {
  const out = [];
  for (const line of String(text ?? '').split('\n')) {
    let last = 0;
    /* Walk runs of adjacent [Tag]s. A run starts a clause when any tag in it
     * is a clause tag; the run belongs to the clause it introduces. */
    const re = /\[[^\]]*\](?:\s*\[[^\]]*\])*/g;
    let m;
    while ((m = re.exec(line))) {
      const tags = m[0].match(/\[[^\]]*\]/g) || [];
      if (!tags.some(t => CLAUSE_TAG.test(t.slice(1, -1).trim()))) continue;
      if (m.index > last) out.push(line.slice(last, m.index));
      last = m.index;
    }
    out.push(line.slice(last));
  }
  return out.map(s => s.trim()).filter(s => s && s !== '-');
}

/* ---------------------------------------------------------------------- */
/* keywords                                                                */
/* ---------------------------------------------------------------------- */

/**
 * Keywords a card has. `rush` is only the unconditional printing — a card that
 * merely "gains [Rush]" under a condition reports `rushIf` with the DON!!
 * threshold that switches it on.
 */
/* Memoised on the card OBJECT, for the reasons spelled out on `staticCache`
 * below: keywords() is read on every attack, target enumeration and blocker
 * check, and splitting the text into clauses made an uncached call 44% slower
 * (1.26M/sec -> 0.70M/sec over 80k calls). Cached it costs a WeakMap hit, and
 * object identity keeps a replaced database from serving a stale parse. */
const keywordCache = new WeakMap();

export function keywords(c) {
  if (c && keywordCache.has(c)) return keywordCache.get(c);
  const out = readKeywords(c);
  if (c) keywordCache.set(c, out);
  return out;
}

function readKeywords(c) {
  const t = cardText(c);
  const has = re => re.test(t);

  // "[DON!! x2] This Character gains [Rush]" — capture the actual N.
  let rushIf = null;
  let rushRequire = null;
  const gainsRush = /gains \[Rush\]/i.test(t);
  const clauses = splitClauses(t);
  if (gainsRush) {
    const line = clauses.find(l => /gains \[Rush\]/i.test(l)) || t;
    /* Only an always-on clause grants Rush continuously. 33 cards read
     * "[On Play] You may rest 1 DON!!: This Character gains [Rush] during this
     * turn" — Rush there is the payoff of an effect you have to resolve, and
     * treating it as a keyword handed those cards permanent Rush from the
     * moment they hit the field. Timed clauses emit a gainKeyword op instead. */
    if (!EVENT_TAG.test(line)) {
      const donx = line.match(/\[DON!!\s*x\s*(\d+)\]/i);
      rushIf = donx ? num(donx[1]) : 0;   // 0 = conditional but not DON-gated
      // "If you have 6 or less DON!! cards…, this Character gains [Rush]" —
      // the condition is the whole point, and was being ignored.
      rushRequire = parseCondition(line);
      /* A condition we cannot read, on a clause with no DON!! gate, leaves
       * nothing to test. Granting Rush anyway is the aggressive guess; it is
       * also the one that lets a card attack on a turn it should not. */
      if (rushIf === 0 && !rushRequire && /\bif\b/i.test(line.replace(/\[[^\]]*\]/g, ' '))) rushIf = null;
    }
  }

  return {
    blocker: has(/\[Blocker\]/i),
    /* Printed [Rush] heads a clause, or follows the end of one — [Rush] is not
     * a clause tag, so a card that prints an ability first and its keyword
     * after ("…−1000 power. [Rush]") keeps them on one line. Requiring the end
     * of a sentence is what separates that from "…Characters gain [Rush]",
     * which grants Rush to other cards and not to this one. */
    rush: !gainsRush && clauses.some(l =>
      /^\[Rush\](?!:)/i.test(l) || /[.)]\s+\[Rush\](?!:)/i.test(l)),
    rushIf,
    rushRequire,
    /* The same trap as plain [Rush], one keyword over. `[Rush: Character]` was
     * read wherever it appeared in the text, so a card whose *activated*
     * ability grants it — EB04-007 Zoro, gated on the opponent having an
     * 8000-power Character — could swing the turn it landed without paying
     * that gate. A card that "gains" it emits a gainKeyword op instead, from
     * the clause that grants it.
     *
     * An always-on clause that grants it conditionally would be understated by
     * this, the way `rushIf` is not for plain Rush. No printed card words it
     * that way today, and understating is the safe direction. */
    rushChar: !/gains \[Rush: Character\]/i.test(t) && has(/\[Rush: Character\]/i),
    doubleAttack: has(/\[Double Attack\]/i),
    banish: has(/\[Banish\]/i),
  };
}

/** The DON!! threshold on a clause, e.g. `[DON!! x2] …` → 2. */
export const donRequirement = line => {
  const m = String(line).match(/\[DON!!\s*x\s*(\d+)\]/i);
  return m ? num(m[1]) : 0;
};

/* ---------------------------------------------------------------------- */
/* clause parsing                                                          */
/* ---------------------------------------------------------------------- */

/* Bandai's grammar puts an activation cost before a colon:
 *     "[On Play] You may trash 1 card from your hand: Draw 2 cards."
 * The part before the colon is a cost the player chooses to pay; the part after
 * is the payoff. The POC matched both halves against the same op table, so it
 * discarded AND drew with no choice offered. */
function splitCost(body) {
  const m = body.match(/^(.*?(?:You may |DON!!\s*[-−]\d+|Don!!\s*[-−]\d+).*?):\s*(.+)$/i);
  if (!m) return { cost: null, effect: body };
  return { cost: parseCost(m[1]), effect: m[2] };
}

/* Field names here must match what the engine's payCost/canPayCost consume —
 * a cost parsed under a name nothing reads is a cost never charged. */
function parseCost(text) {
  const cost = { text: text.trim(), optional: /you may/i.test(text) };
  let m;
  if ((m = text.match(new RegExp(String.raw`DON!!\s*${DASH}\s*(\d+)`, 'i')))) cost.don = num(m[1]);
  if ((m = text.match(/trash (\d+) cards? from your hand/i))) cost.discard = num(m[1]);
  if ((m = text.match(/trash (\d+) cards? from the top of your deck/i))) cost.mill = num(m[1]);
  if ((m = text.match(/rest (\d+) of your DON!! cards?/i))) cost.restDon = num(m[1]);
  if (/rest this/i.test(text)) cost.restSelf = true;
  if (/trash this/i.test(text)) cost.trashSelf = true;
  if ((m = text.match(/turn (\d+) cards? from the top .*?of your Life/i))) cost.lifeFlip = num(m[1]);
  return cost;
}

/* ---------------------------------------------------------------------- */
/* printed conditions                                                      */
/* ---------------------------------------------------------------------- */

/* Bandai gates a great many clauses on a leading condition:
 *   "[On Play] If your Leader is [Enel], draw 1 card."
 *   "[When Attacking] If you have 6 or less DON!! cards on your field, …"
 * 828 clauses across 398 cards read this way, and the parser threw all of it
 * away — every one of those effects fired unconditionally.
 *
 * The engine already evaluates conditions for hand-written scripts, through
 * `meetsRequirements` and a clause's `require`. These emit the same shape, so
 * the parser gets the same evaluator rather than a second one.
 *
 * Deliberately fails OPEN. A condition we cannot read emits no `require` at
 * all, which leaves the clause firing exactly as it does today. That keeps a
 * misread condition from silently disabling a card — the failure mode the
 * script notes call worse than no model at all. */
const CONDITIONS = [
  [/^your Leader is \[([^\]]+)\]$/i, m => ({ leaderName: m[1] })],
  [/^your Leader has the \{([^}]+)\} type$/i, m => ({ leaderType: m[1] })],
  /* "…type INCLUDES \"CP\"" is a substring test, which is why Bandai words it
   * differently from "has the {X} type". Real leaders carry CP0, CP9 and
   * GERMA 66; matching those exactly finds nothing and switches the card off. */
  [/^your Leader's type includes "([^"]+)"$/i, m => ({ leaderTypeIncludes: m[1] })],
  [/^you have (\d+) or less Life cards?$/i, m => ({ lifeAtMost: num(m[1]) })],
  [/^you have (\d+) or more Life cards?$/i, m => ({ lifeAtLeast: num(m[1]) })],
  [/^you have (\d+) or less cards in your hand$/i, m => ({ handAtMost: num(m[1]) })],
  [/^you have (\d+) or more cards in your hand$/i, m => ({ handAtLeast: num(m[1]) })],
  [/^you have (\d+) or more cards in your trash$/i, m => ({ trashAtLeast: num(m[1]) })],
  [/^you have (\d+) or more Events? in your trash$/i, m => ({ trashEventsAtLeast: num(m[1]) })],
  [/^you have (\d+) or more DON!! cards on your field$/i, m => ({ minDonTotal: num(m[1]) })],
  [/^you have (\d+) or less DON!! cards on your field$/i, m => ({ maxDonTotal: num(m[1]) })],
  [/^you have (\d+) or more rested Characters?$/i, m => ({ restedCharsAtLeast: num(m[1]) })],
  [/^your opponent has (\d+) or more rested Characters?$/i, m => ({ oppRestedCharsAtLeast: num(m[1]) })],
  [/^your opponent has (\d+) or more cards in their hand$/i, m => ({ oppHandAtLeast: num(m[1]) })],
  /* Power, not count — "a Character with 8000 power or more" asks about the
   * board's biggest body, and it means *current* power, so DON!! attached to
   * it counts. Every other condition here counts things. */
  [/^your opponent has an? Character with (\d[\d,]*) power or more$/i, m => ({ oppCharPowerAtLeast: num(m[1]) })],
  [/^your opponent has (\d+) or less Life cards?$/i, m => ({ oppLifeAtMost: num(m[1]) })],
  [/^you have any DON!! cards given$/i, () => ({ donGiven: true })],
  [/^the only Characters on your field are \{([^}]+)\} type Characters$/i, m => ({ allCharsOfType: m[1] })],
];

/**
 * The condition a clause opens with, as a `require` object — or null.
 *
 * Null means one of two different things, and both must behave the same way:
 * the clause has no condition, or it has one this cannot read. Either way the
 * caller attaches nothing and the clause keeps its current behaviour.
 */
export function parseCondition(body) {
  // Only a condition that opens the clause gates the whole clause. A mid-
  // sentence "Then, if …" gates one sentence, which is a per-op question and
  // is left to hand-written scripts.
  const m = String(body).replace(/^[^:]*:\s*/, '').match(/^(?:If|When) ([^,]{4,120}),/i);
  if (!m) return null;

  /* "If this Character would be K.O.'d…" is a replacement effect, not a
   * precondition — reading it as one would gate the clause on something that
   * is never true at enqueue time and switch the card off entirely. */
  if (/^this (?:Character|Leader|card)\b/i.test(m[1])) return null;

  const out = {};
  for (const part of m[1].split(/\s+and\s+/i)) {
    const clean = part.trim().replace(/\.$/, '');
    const rule = CONDITIONS.find(([re]) => re.test(clean));
    if (!rule) return null;                 // one unreadable part fails the lot
    Object.assign(out, rule[1](clean.match(rule[0])));
  }
  return Object.keys(out).length ? out : null;
}

/** Recognised operations in one clause. Empty means "we didn't understand it". */
function parseOps(line) {
  const ops = [];
  const R = (body, flags = 'i') => new RegExp(body.replace(/OPP/g, OPP).replace(/DASH/g, DASH), flags);
  const optional = /up to|you may/i.test(line);
  let m;

  // --- card flow ---
  // Guard against giving the opponent's draw to the active player.
  if ((m = line.match(R(String.raw`your OPP draws? (\d+) cards?`)))) {
    ops.push({ op: 'draw', n: num(m[1]), side: 'opp' });
  } else if ((m = line.match(/draw (\d+) cards?/i))) {
    ops.push({ op: 'draw', n: num(m[1]), side: 'self' });
  }

  if ((m = line.match(R(String.raw`your OPP trash(?:es)? (\d+) cards? from their hand`)))) {
    ops.push({ op: 'discard', n: num(m[1]), side: 'opp' });
  }

  if ((m = line.match(/look at (\d+) cards? from the top of your deck/i))) {
    const type = line.match(/reveal up to \d+ \{([^}]+)\}/i) || line.match(/reveal up to \d+ ([A-Za-z ]+?) type/i);
    ops.push({ op: 'dig', n: num(m[1]), type: type ? type[1].trim() : null });
  }

  // --- removal ---
  if ((m = line.match(R(String.raw`K\.?O\.? up to (\d+) of your OPP Characters? with (?:a )?cost of (\d+) or less`)))) {
    ops.push({ op: 'ko', n: num(m[1]), maxCost: num(m[2]), optional });
  } else if ((m = line.match(R(String.raw`K\.?O\.? up to (\d+) of your OPP Characters? with (\d[\d,]*) (?:base )?power or less`)))) {
    ops.push({ op: 'ko', n: num(m[1]), maxPower: num(m[2]), optional });
  } else if ((m = line.match(R(String.raw`K\.?O\.? up to (\d+) of your OPP Characters?`)))) {
    ops.push({ op: 'ko', n: num(m[1]), optional });
  }

  if ((m = line.match(R(String.raw`rest up to (\d+) of your OPP Characters?`)))) {
    ops.push({ op: 'rest', n: num(m[1]), side: 'opp', optional });
  }

  if ((m = line.match(R(String.raw`place up to (\d+) of your OPP Characters?.*?(?:bottom|top) of the owner['’]?s deck`)))) {
    ops.push({ op: 'bounce', n: num(m[1]), optional });
  }

  // --- power modification ---
  if ((m = line.match(R(String.raw`give up to (\d+) of your OPP Characters? DASH(\d[\d,]*) power`)))) {
    ops.push({ op: 'power', n: num(m[1]), amount: -num(m[2]), side: 'opp', optional });
  }
  if ((m = line.match(/give up to (\d+) of your Characters? \+(\d[\d,]*) power/i))) {
    ops.push({ op: 'power', n: num(m[1]), amount: num(m[2]), side: 'self', optional });
  }
  /* "Up to 1 of your Leader or Character cards gains +1000 power" — a single
   * chosen unit, and the Leader is one of the choices. 135 cards word a buff
   * this way and none of them matched any rule here, so the whole clause read
   * as unparsed. Must be tested before the board-wide form below, whose
   * "your Leader or Characters gain" is the same words without "up to". */
  if ((m = line.match(/up to (\d+) of your Leader or Character cards? gains? \+(\d[\d,]*) power/i))) {
    ops.push({ op: 'power', n: num(m[1]), amount: num(m[2]), side: 'self', withLeader: true, optional: true });
  } else if ((m = line.match(/your Leader (?:or|and) (?:all of your )?Characters? gains? \+(\d[\d,]*) power/i))) {
    ops.push({ op: 'power', n: 1, amount: num(m[1]), side: 'self', target: 'leader' });
  }
  if ((m = line.match(/this Character gains \+(\d[\d,]*) power/i))) {
    ops.push({ op: 'power', n: 1, amount: num(m[1]), side: 'self', target: 'this' });
  }

  /* "This Character gains [Rush] during this turn" as the payoff of a timed
   * clause. Read as a keyword it made the card permanently hasty; as an op it
   * lands only when the clause it is printed on actually resolves. */
  if ((m = line.match(/this Character gains \[(Rush: Character|Rush|Double Attack|Banish)\]/i))) {
    // "Rush: Character" is a distinct keyword, not Rush with a suffix — it
    // permits the attack and forbids the Leader as its target.
    const kind = /^rush: character$/i.test(m[1])
      ? 'rushChar'
      : m[1].toLowerCase().replace(' ', '');
    ops.push({ op: 'gainKeyword', kind, target: 'this', scope: 'turn' });
  }

  // --- DON!! economy ---
  /* One clause can add DON!! twice with different states, e.g. Enel:
   *   "add up to 1 DON!! card from your DON!! deck and set it as active,
   *    and add up to 4 additional DON!! cards and rest them"
   * Matching only the first half turned a 5-DON!! ramp into 1, which on a
   * 6-card DON!! deck is the difference between the archetype working and not.
   * So walk every "add up to N …" and read each one's own tail for its state. */
  const donAdds = [...line.matchAll(
    /add up to (\d+) (?:additional )?DON!! cards?(?: from your DON!! deck)?((?:(?!add up to)[^.;])*)/gi
  )];
  for (const a of donAdds) {
    ops.push({ op: 'donAdd', n: num(a[1]), rested: /rest (?:it|them)/i.test(a[2] || '') });
  }

  // Only an explicit "set N of your DON!! cards as active" flips existing DON!!;
  // "set it as active" refers to a card just added and is handled above.
  if ((m = line.match(/set (?:up to )?(\d+) of your DON!! cards? as active/i))) {
    ops.push({ op: 'donActive', n: num(m[1]) });
  }

  if ((m = line.match(/give up to (\d+) rested DON!! cards? to (your Leader|1 of your [^.,;]*)/i))) {
    ops.push({ op: 'donGive', n: num(m[1]), to: /leader/i.test(m[2]) ? 'leader' : 'character' });
  }

  // --- deployment ---
  /* Bandai writes this several ways, and requiring the literal words
   * "Character card" missed a whole class of cards:
   *   "play up to 1 {Straw Hat Crew} type Character card with a cost of 5 or less"
   *   "play up to 1 [Prisoner of Impel Down] card from your hand"
   *   "play up to 1 Character card with 8000 power or less"
   * Match the shape instead, then read the qualifiers out of the clause. */
  if ((m = line.match(/play up to (\d+) (.*?)\bcards?\b/i))) {
    const qualifier = m[2] || '';
    const cost = line.match(/cost of (\d+) or less/i);
    const power = line.match(/(\d[\d,]*) power or less/i);
    const named = qualifier.match(/\[([^\]]+)\]/);       // [Prisoner of Impel Down]
    const typed = qualifier.match(/\{([^}]+)\}/);        // {Straw Hat Crew}
    ops.push({
      op: 'playFree', n: num(m[1]), optional: true,
      maxCost: cost ? num(cost[1]) : null,
      maxPower: power ? num(power[1]) : null,
      fromHandName: named ? named[1] : null,
      type: typed ? typed[1] : null,
      from: /from your (?:hand or trash|trash)/i.test(line) ? 'handOrTrash' : 'hand',
    });
  }

  // --- life ---
  if (/add up to 1 card from the top of your deck to the top of your Life cards/i.test(line)) {
    ops.push({ op: 'lifeAdd', n: 1, optional });
  }

  return ops;
}

/* When a clause fires. */
const TIMINGS = [
  [/^\[On Play\]/i, 'onPlay'],
  [/^\[Main\]/i, 'onPlay'],                 // Events print [Main]
  [/^\[When Attacking\]/i, 'whenAttacking'],
  [/^\[On K\.?O\.?\]/i, 'onKO'],
  [/^\[On Block\]/i, 'onBlock'],
  [/^\[Activate: Main\]/i, 'activate'],
  [/^\[Counter\]/i, 'counter'],
  [/^\[Trigger\]/i, 'trigger'],
  [/^\[End of (?:Your )?Turn\]/i, 'endOfTurn'],
  [/^\[On Your Opponent['’]s Attack\]/i, 'onOppAttack'],
  [/^\[Your Turn\]/i, 'passive'],
  [/^\[Opponent['’]s Turn\]/i, 'passive'],
  [/^\[DON!!\s*x\s*\d+\]/i, 'passive'],
];

/**
 * Split a card into timed clauses.
 * Each clause: { when, text, don, cost, ops, optional, oncePerTurn, unparsed }
 */
export function parseEffects(c) {
  const out = [];
  const raw = cardText(c);
  if (!raw || raw === '-') return out;

  for (const L of splitClauses(raw)) {
    // A clause can carry several leading tags: "[Your Turn] [Once Per Turn] [On Play] …"
    let when = null;
    for (const [re, name] of TIMINGS) {
      if (re.test(L)) { when = name; break; }
    }
    // A tag can also appear mid-line after a condition tag.
    if (!when || when === 'passive') {
      for (const [re, name] of TIMINGS) {
        if (name === 'passive') continue;
        if (new RegExp(re.source.replace(/^\^/, ''), 'i').test(L)) { when = name; break; }
      }
    }
    if (!when) when = 'passive';

    // Strip every leading [Tag] to get at the sentence.
    const body = L.replace(/^(?:\s*\[[^\]]+\]\s*)+/, '').trim() || L;
    const { cost, effect } = splitCost(body);
    const ops = parseOps(effect);

    out.push({
      when,
      text: L,
      body,
      don: donRequirement(L),
      oncePerTurn: /\[Once Per Turn\]/i.test(L),
      cost,
      require: parseCondition(body),
      ops,
      optional: /up to|you may/i.test(effect),
      // A passive line with no ops is flavour//static text, not a missing feature.
      unparsed: ops.length === 0 && when !== 'passive',
    });
  }
  return out;
}

/* ---------------------------------------------------------------------- */
/* card-level helpers                                                      */
/* ---------------------------------------------------------------------- */

/** Counter value usable in the counter step: printed value, or an Event's text. */
/**
 * Parse the `[Trigger]` clause, which lives in its own field.
 *
 * `parseEffects` reads `cardText(c)`, i.e. `c.effect` — the trigger is a
 * separate field in the dump and was never parsed at all, which is why every
 * activated trigger fell through to a generic manual menu.
 *
 * Returns a clause in the same shape `parseEffects` produces, or null. A
 * clause it cannot read comes back with `unparsed` set, so the caller can
 * still fall back rather than silently dropping the effect.
 */
export function parseTrigger(c) {
  const t = triggerText(c);
  if (!t || t === '-') return null;
  return parseEffects({ ...c, effect: t }).find(e => e.when === 'trigger') ?? null;
}

export function counterValue(c) {
  if (!c) return 0;
  if (c.counter) return c.counter;
  const t = cardText(c);
  if (!/\[Counter\]/i.test(t)) return 0;
  const m = t.match(/\+(\d[\d,]*) power during this battle/i);
  return m ? num(m[1]) : 0;
}

/** Leaders may shrink the DON!! deck (Enel runs 6 instead of 10). */
export function donDeckSize(leader) {
  const m = cardText(leader).match(/DON!!\s*deck consists of (\d+)/i);
  return m ? num(m[1]) : 10;
}

/** Life total is printed in the Leader's `cost` field in the punk-records dump. */
export const lifeTotal = leader => (leader && leader.cost) || 5;

/** Cards that explicitly opt out of the 4-copy rule. */
export const copyLimit = c => /any number of .*in your deck/i.test(cardText(c)) ? 99 : 4;

/* ---------------------------------------------------------------------- */
/* continuous ("static") power buffs                                       */
/* ---------------------------------------------------------------------- */

/**
 * Always-on power modifiers printed on a card, e.g.
 *   "[DON!! x1] [Your Turn] All of your Characters gain +1000 power."
 *   "[Opponent's Turn] This Character gains +2000 power."
 *
 * These are not events — they apply whenever their condition holds, so the
 * engine reads them on every power calculation rather than pushing a mod.
 * Ignoring them meant a Leader's whole board was quietly under-powered.
 *
 * @returns {{amount:number, target:'this'|'allChars'|'leaderAndChars', when:'always'|'yourTurn'|'oppTurn', don:number}[]}
 */
/* Keyed on the card OBJECT, not its id.
 *
 * An id-only key served a stale parse whenever a card's text changed under a
 * known id — the database is replaceable at runtime through setCards(), and
 * fixtures reuse ids with different text — which is a wrong board-wide power
 * number and no error anywhere.
 *
 * A WeakMap rather than a composite string key: this runs on every power read,
 * and building `id + fullCardText` per call measured 42% slower over a
 * 800-game gauntlet (8.1s -> 11.5s). Object identity costs nothing and gives
 * the same isolation, since a replaced database brings new objects. Entries
 * also fall out with the cards themselves. */
const staticCache = new WeakMap();

export function staticBuffs(c) {
  if (!c) return [];
  if (staticCache.has(c)) return staticCache.get(c);

  const out = [];
  for (const line of splitClauses(cardText(c))) {
    // Only lines that are purely conditional tags + a "gains +N power" clause.
    // Anything with an event tag is handled by parseEffects instead.
    if (/\[(On Play|On K\.?O\.?|When Attacking|Activate|Counter|Trigger|On Block|On Your Opponent)/i.test(line)) continue;

    const m = line.match(/gains? \+(\d[\d,]*) power/i);
    if (!m) continue;
    const amount = num(m[1]);
    if (!amount) continue;

    const bare = line.replace(/\[[^\]]*\]/g, ' ');

    /* Skip anything whose magnitude or condition we cannot evaluate. Applying
     * a buff we half-understand overstates power, which is worse than missing
     * it — an understated board is at least conservative.
     *
     * A condition we CAN evaluate is no longer a reason to skip: it becomes a
     * `require` the power calculation checks, the same object the timed
     * clauses use. That is what puts the +2000 back on the cards whose whole
     * identity is a conditional stat line. */
    const require = parseCondition(line);
    if (/\bif\b/i.test(bare) && !require) continue;  // "if you have 5 or more…"
    if (/for every|for each/i.test(bare)) continue;  // scales with a count

    const when = /\[Your Turn\]/i.test(line) ? 'yourTurn'
      : /\[Opponent['’]s Turn\]/i.test(line) ? 'oppTurn'
        : 'always';

    /* Who receives it. A board-wide clause ("All of your …") is frequently
     * restricted by card name, type, colour or cost — reading those out is the
     * difference between buffing the right bodies and buffing the source. */
    /* Capture everything between "All of your" and "gain +N", which is the
     * full scope description — the noun can be followed by qualifiers
     * ("…Characters with a base cost of 4 or more gain +1000 power"). */
    const board = line.match(/all of your\s+(.*?)\s+gains?\s+\+\d/i);
    let entry;
    if (board && /\b(cards?|characters?)\b/i.test(board[1])) {
      const scopeText = board[1];
      const names = [...scopeText.matchAll(/\[([^\]]+)\]/g)].map(x => x[1]);
      const types = [...scopeText.matchAll(/\{([^}]+)\}/g)].map(x => x[1]);
      const minCost = scopeText.match(/base cost of (\d+) or more/i);
      const colour = scopeText.match(/\b(red|green|blue|purple|black|yellow)\b/i);
      entry = {
        amount, when, require, don: donRequirement(line), target: 'allChars',
        names: names.length ? names : null,
        types: types.length ? types : null,
        colour: colour ? colour[1].toLowerCase() : null,
        minBaseCost: minCost ? num(minCost[1]) : null,
      };
    } else if (/your Leader (?:or|and) (?:all of your )?Characters?/i.test(line)) {
      entry = { amount, when, require, don: donRequirement(line), target: 'leaderAndChars' };
    } else if (/this (?:Character|Leader)/i.test(line)) {
      entry = { amount, when, require, don: donRequirement(line), target: 'this' };
    } else {
      continue;    // an addressing form we do not recognise — leave it alone
    }

    out.push(entry);
  }

  staticCache.set(c, out);
  return out;
}
